import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { getSharedQuote } from "@/lib/quote-shares.functions";
import { quoteRowToPdfInput, type SavedQuote } from "@/lib/saved-quotes";
import { downloadQuotePdf } from "@/lib/quote-pdf";

export const Route = createFileRoute("/quote/$token")({
  loader: ({ params }) => getSharedQuote({ data: { token: params.token } }),
  head: () => ({
    meta: [
      { title: "Your Finendra Quote" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "A private Finendra pricing quote shared with you." },
    ],
  }),
  errorComponent: () => (
    <Shell
      title="Link unavailable"
      body="This quote link could not be opened. Ask your Finendra contact for a fresh link."
    />
  ),
  notFoundComponent: () => (
    <Shell title="Link not found" body="This quote link is no longer valid." />
  ),
  component: SharedQuotePage,
});

function Shell({ title, body }: { title: string; body: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-24">
      <div className="max-w-md rounded-3xl border border-brand-navy/10 bg-background p-10 text-center">
        <h1 className="font-display text-3xl font-bold text-brand-navy">{title}</h1>
        <p className="mt-4 text-brand-navy/60">{body}</p>
        <a
          href="/contact"
          className="mt-8 inline-block rounded-xl bg-brand-navy px-6 py-3 text-sm font-bold text-background hover:bg-brand-accent"
        >
          Contact Finendra
        </a>
      </div>
    </main>
  );
}

const usd = (n: number | null | undefined) =>
  n === null || n === undefined
    ? "—"
    : n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });

function SharedQuotePage() {
  const result = Route.useLoaderData();
  const [busy, setBusy] = useState<null | "pdf" | "docx">(null);

  if (!result) {
    return (
      <Shell
        title="Link not found"
        body="This quote link is no longer valid — it may have been revoked."
      />
    );
  }
  if (result.expired) {
    return (
      <Shell
        title="This link has expired"
        body="For security, quote links expire after a set period. Ask your Finendra contact to send a new one."
      />
    );
  }

  const quote = { ...result.quote, user_id: null } as SavedQuote;
  const input = quoteRowToPdfInput(quote);

  async function download(kind: "pdf" | "docx") {
    setBusy(kind);
    try {
      if (kind === "pdf") await downloadQuotePdf(input);
      else {
        const { downloadQuoteDocx } = await import("@/lib/quote-docx");
        await downloadQuoteDocx(input);
      }
    } finally {
      setBusy(null);
    }
  }

  const rows: Array<[string, string]> = [
    ["Plan", quote.plan_name],
    ["Monthly hours", `${quote.hours} hrs`],
    ["Scope", quote.work_level],
    ["Hourly rate", usd(quote.hourly_rate)],
    ["Monthly retainer", usd(quote.monthly_rate)],
    ["Billing", quote.use_retainer ? "Monthly retainer" : "Hourly"],
    ["Estimated monthly cost", quote.custom ? "Custom scope" : usd(quote.estimate)],
  ];

  return (
    <main className="min-h-screen px-6 pt-24 md:pt-32 pb-28">
      <div className="mx-auto max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
          Private quote
        </p>
        <h1 className="mt-3 font-display text-4xl font-bold text-brand-navy">Finendra estimate</h1>
        {(quote.client_name || quote.client_company) && (
          <p className="mt-3 text-brand-navy/60">
            Prepared for {quote.client_name}
            {quote.client_name && quote.client_company ? " — " : ""}
            {quote.client_company}
          </p>
        )}

        <dl className="mt-10 divide-y divide-brand-navy/10 overflow-hidden rounded-3xl border border-brand-navy/10">
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-6 bg-background px-6 py-4">
              <dt className="text-sm text-brand-navy/60">{label}</dt>
              <dd className="text-sm font-bold text-brand-navy">{value}</dd>
            </div>
          ))}
        </dl>

        {quote.note && (
          <p className="mt-6 rounded-2xl bg-brand-navy/5 p-6 text-sm text-brand-navy/70">
            {quote.note}
          </p>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => download("pdf")}
            disabled={busy !== null}
            className="rounded-xl bg-brand-navy px-6 py-3 text-sm font-bold text-background hover:bg-brand-accent disabled:opacity-50"
          >
            {busy === "pdf" ? "Preparing…" : "Download PDF"}
          </button>
          <button
            type="button"
            onClick={() => download("docx")}
            disabled={busy !== null}
            className="rounded-xl border border-brand-navy/15 px-6 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent disabled:opacity-50"
          >
            {busy === "docx" ? "Preparing…" : "Download Word"}
          </button>
        </div>

        <p className="mt-8 text-xs text-brand-navy/40">
          This private link expires on{" "}
          {new Date(result.expiresAt).toLocaleDateString("en-US", { dateStyle: "long" })}.
        </p>
      </div>
    </main>
  );
}
